import React, { useCallback, useEffect, useState } from 'react';
import MetaTags from '../components/MetaTags';
import { useQueryString } from '../hooks/useQueryString';
import { webConfig } from '../utils/webConfig';
import { GivelifyLogo } from '../components/icons';
import { StyledActivateDiv } from './Activate';
import { ApiHandler } from '../api';
import { HeartLoading } from '../components/HeartLoading';
import { useDonorProfile } from '../api';
import { navigateToHomepage } from '../utils/navigation';
import { Button } from '../components';

const unableToActivate =
    'Unable to activate your account, please try generating another link below.';
const error = (
    <>
        We are unable to resend another link at this time. Please contact our{' '}
        <a
            href={webConfig.supportContactUrl}
            rel={'noreferrer'}
        >
            support team
        </a>{' '}
        if the issue persists.
    </>
);

const renderResendFailureState = (
    message: React.ReactNode,
    onClick: () => void,
) => (
    <>
        <div className={'failure-box'}>{message}</div>
        <Button
            onClick={onClick}
            className="w-full"
        >
            Send another link
        </Button>
    </>
);

const activateLoadingState = <HeartLoading className={'heart-loading'} />;

const ActivateEmail: React.FC = () => {
    const { donorProfile } = useDonorProfile();
    const isLoggedIn = Boolean(donorProfile);
    const query = useQueryString();
    const token = query.get('token') || query.get('id');
    const [activateState, setActivateState] = useState<JSX.Element | null>(
        activateLoadingState,
    );

    const handleHomePage = useCallback(() => {
        navigateToHomepage(isLoggedIn);
    }, [isLoggedIn]);

    const resendEmail = useCallback(async () => {
        setActivateState(activateLoadingState);
        const apiRequest = await ApiHandler.instance.auth.activateDonorEmail({
            token,
            resend: true,
        });
        if (apiRequest.success) {
            setActivateState(
                <>
                    <div className={'success-box'}>
                        Activation sent! Please check your inbox for an email
                        from Givelify and click the link there.
                    </div>
                    <Button
                        onClick={handleHomePage}
                        className="w-full"
                    >
                        Go to homepage
                    </Button>
                </>,
            );
        } else {
            setActivateState(renderResendFailureState(error, resendEmail));
        }
    }, [token, handleHomePage]);

    const makeActivationCall = useCallback(async () => {
        setActivateState(activateLoadingState);
        const apiRequest = await ApiHandler.instance.auth.activateDonorEmail({
            token,
            resend: false,
        });
        if (apiRequest.success) {
            setActivateState(
                <>
                    <div className={'success-box'}>
                        You have successfully activated your email address. You
                        may close this tab.
                    </div>
                    <Button
                        onClick={handleHomePage}
                        className="w-full"
                    >
                        Go to homepage
                    </Button>
                </>,
            );
        } else {
            setActivateState(
                renderResendFailureState(unableToActivate, resendEmail),
            );
        }
    }, [token, handleHomePage, resendEmail]);

    useEffect(() => {
        makeActivationCall();
        //listen only to token update
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return (
        <StyledActivateDiv>
            <GivelifyLogo className={'givelify-logo'} />
            <MetaTags
                title={'Activate Account'}
                description={'Activate your account with Givelify'}
            />
            {activateState}
        </StyledActivateDiv>
    );
};

export default ActivateEmail;
